import {useSelector} from 'react-redux';
import React, {useEffect, useState} from 'react';
import _ from 'lodash';
import {useGetDocumentQuery} from '../../data/documentWorkspaceApi';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {DARK_TEXT_COLOR} from '../../theme';
import PropTypes from 'prop-types';
import {INGESTION_STATUSES} from '../../constants';

const styles = {
  processing: {
    header: {
      fontSize: '0.875rem',
      fontWeight: '400',
      lineHeight: '140%',
      color: DARK_TEXT_COLOR,
    },
    processingLabel: {
      color: '#EC881B',
      fontSize: '0.75rem',
      fontWeight: '400',
      lineHeight: '140%',
    },
    completedLabel: {
      color: '#043B34',
      fontSize: '0.75rem',
      fontWeight: '400',
      lineHeight: '140%',
    },
    failedLabel: {
      color: '#043B34',
      fontSize: '0.75rem',
      fontWeight: '400',
      lineHeight: '140%',
    },
    label: {
      color: DARK_TEXT_COLOR,
      fontSize: '0.875rem',
      fontWeight: '600',
      lineHeight: '140%',
    },
  },
};

const Processing = ({isError, isSuccess}) => {
  if (isSuccess) {
    return null;
  }

  if (isError) {
    return (
      <Stack
        direction={'row'}
        spacing={'0.5rem'}
        width={'5.5rem'}
        alignItems={'center'}
        justifyContent={'flex-end'}
      >
        <Typography style={styles.processing.failedLabel}>Failed</Typography>
        <ErrorOutlineIcon fontSize={'1.25rem'} sx={{color: '#f44336'}} />
      </Stack>
    );
  }

  return (
    <Stack
      direction={'row'}
      spacing={'0.5rem'}
      width={'5.5rem'}
      alignItems={'center'}
      justifyContent={'flex-end'}
    >
      <Typography style={styles.processing.processingLabel}>
        Processing
      </Typography>
      <CircularProgress size={'1.25rem'} sx={{color: '#EC881B'}} />
    </Stack>
  );
};

Processing.propTypes = {
  isProcessing: PropTypes.bool.isRequired,
  isSuccess: PropTypes.bool.isRequired,
  isError: PropTypes.bool.isRequired,
};

export const ProcessingContainer = ({
  documentId,
  onComplete,
  setIngestionStatus,
  setIngestionError,
}) => {
  const {isAuthenticated} = useSelector((state) => state.session);
  const [skip, setSkip] = useState(true);
  const [numFailures, setNumFailures] = useState(0);

  useEffect(() => {
    setSkip(_.isNil(documentId) || !isAuthenticated);
  }, [documentId, isAuthenticated]);

  const {data, isLoading, isError} = useGetDocumentQuery(
    {documentId},
    {refetchOnMountOrArgChange: true, skip, pollingInterval: 5000}
  );

  useEffect(() => {
    const document = _.get(data, 'record');
    if (!document) return;

    switch (document?.ingestionState?.type) {
      case 'upload': {
        const uploadStatus = document?.ingestionState?.status;
        const uploadError = document?.ingestionState?.error;
        if (!_.isNil(uploadStatus)) {
          setIngestionStatus(uploadStatus);
          if (uploadStatus === INGESTION_STATUSES.completed) {
            onComplete(document);
            setSkip(true);
          } else if (uploadStatus === INGESTION_STATUSES.failed) {
            if (setIngestionError && uploadError) {
              setIngestionError(uploadError);
            }
            setSkip(true);
          }
        }
        break;
      }
      case 'rollforward': {
        const rollforwardStatus = document?.ingestionState?.status;
        const rollforwardError = document?.ingestionState?.error;
        if (!_.isNil(rollforwardStatus)) {
          setIngestionStatus(rollforwardStatus);
          if (rollforwardStatus === INGESTION_STATUSES.completed) {
            onComplete(document);
            setSkip(true);
          } else if (rollforwardStatus === INGESTION_STATUSES.failed) {
            if (setIngestionError && rollforwardError) {
              setIngestionError(rollforwardError);
            }
            setSkip(true);
          }
        }
        break;
      }
    }
  }, [data, documentId]);

  useEffect(() => {
    if (isError && numFailures === 12) {
      console.error('Testing Too many failures');
      setSkip(true);
    } else {
      setNumFailures(numFailures + 1);
    }
  }, [isError]);

  if (!data) {
    return (
      <Box
        width={'100%'}
        display={'flex'}
        flex={1}
        flexDirection={'column'}
        justifyContent={'center'}
        alignItems={'center'}
      >
        <CircularProgress size={'1.5rem'} sx={{color: '#26A993'}} />
      </Box>
    );
  }

  const ingestionState = _.get(data, 'record.ingestionState');
  const processingItems = [];

  if (ingestionState?.type === 'upload') {
    const skipWorkbookGeneration = ingestionState?.skipWorkbookGeneration;
    if (skipWorkbookGeneration) {
      processingItems.push('Importing your document');
    } else {
      processingItems.push(
        'Importing your document',
        'Creating supporting workbook'
      );
    }
  } else if (ingestionState?.type === 'rollforward') {
    const skipWorkbookGeneration = ingestionState?.skipWorkbookGeneration;
    if (ingestionState?.source === 'PDF') {
      if (skipWorkbookGeneration) {
        processingItems.push(
          'Importing your document',
          'Rolling forward dates and values'
        );
      } else {
        processingItems.push(
          'Importing your document',
          'Rolling forward dates and values',
          'Creating supporting workbook'
        );
      }
    } else if (ingestionState?.source === 'DOCUMENT') {
      processingItems.push(
        'Rolling forward supporting workbook',
        'Rolling forward dates',
        'Rolling forward values',
        'Rolling forward disclosures.'
      );
    }
  }

  // Add autolinking step if enabled
  if (ingestionState?.autoLinking?.enabled) {
    processingItems.push('Establishing autolinking');
  }

  return (
    <Box
      width={'100%'}
      display={'flex'}
      flex={1}
      flexDirection={'column'}
      justifyContent={'center'}
    >
      <List width={'100%'}>
        {processingItems.map((label) => (
          <ListItem
            divider={true}
            key={label}
            sx={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
          >
            <Stack
              direction="row"
              flex={1}
              spacing="0.5rem"
              width="100%"
              justifyContent="space-between"
              height="1.5rem"
              alignItems="center"
            >
              <Typography style={styles.processing.label}>{label}</Typography>
              <Processing
                isProcessing={
                  isLoading ||
                  ingestionState.status === INGESTION_STATUSES.inProgress
                }
                isSuccess={
                  ingestionState.status === INGESTION_STATUSES.completed
                }
                isError={ingestionState.status === INGESTION_STATUSES.failed}
              />
            </Stack>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

ProcessingContainer.propTypes = {
  documentId: PropTypes.string.isRequired,
  onComplete: PropTypes.func.isRequired,
  setIngestionStatus: PropTypes.func.isRequired,
  setIngestionError: PropTypes.func.isRequired,
};
